import { duration, humanise, money } from "../lib/format";
import type { PhaseRecord, PhaseStatus, Playbook } from "../lib/types";
import { StatusBadge, Tag, type StatusTone } from "./primitives";

const TONE: Record<PhaseStatus, StatusTone> = {
  done: "good",
  running: "warning",
  failed: "critical",
  // Not `critical`: the run was stopped, the phase did not go wrong.
  cancelled: "neutral",
  skipped: "neutral",
  pending: "neutral",
};

/**
 * The engagement's spine: the orchestrator's phase sequence, with whatever the
 * run has actually reached overlaid on it. Phases not yet started are shown so
 * the reader can see how much is left, not only what has happened.
 */
export function PhaseTimeline({
  phases,
  playbook,
  plannedPhases,
}: {
  phases: PhaseRecord[];
  playbook?: Playbook;
  /** The run's own record of what it set out to walk. Preferred over the
   * registry: it cannot go stale and it survives the playbook being deleted. */
  plannedPhases?: string[];
}) {
  const byKey = new Map(phases.map((p) => [p.key, p]));

  // The run's own plan first. The registry is the fallback for runs finished
  // before the manifest carried one, and it is a fallback rather than the
  // source because it answers about the playbook *now*: a phase list edited
  // since the run would be drawn against that run, and a playbook deleted
  // since leaves it with no answer at all (#90).
  //
  // Last resort is the observed order, which can only ever show phases that
  // have already started — the opposite of this component's purpose, so it is
  // reached only when there is nothing better.
  const planned = plannedPhases?.length
    ? plannedPhases
    : playbook
      ? playbook.phases.filter((p) => !p.on_demand).map((p) => p.key)
      : phases.filter((p) => p.key !== "revise").map((p) => p.key);

  const revisions = phases.filter((p) => p.key === "revise");

  return (
    <ol className="divide-y divide-line">
      {planned.map((key) => {
        const record = byKey.get(key);
        const status: PhaseStatus = record?.status ?? "pending";
        const meta = playbook?.phases.find((p) => p.key === key);
        return (
          <li key={key} className="flex items-center gap-3 px-4 py-2.5">
            <span className="w-28 shrink-0 text-sm font-medium">{humanise(key)}</span>
            <StatusBadge tone={TONE[status]}>{status}</StatusBadge>
            {meta?.gate && <Tag>gate</Tag>}
            {meta?.local && <Tag>local</Tag>}
            {/* What the machine counted, in its own hand; the word beside
                a count is a person's, in the sans. */}
            <span className="ml-auto flex items-center gap-4 text-xs text-muted">
              {record && record.turns > 0 && (
                <span>
                  <span className="font-mono tabular-nums">{record.turns}</span> turns
                </span>
              )}
              {record && record.duration_s > 0 && (
                <span className="font-mono tabular-nums">{duration(record.duration_s)}</span>
              )}
              {record?.usage?.cost_usd ? (
                <span className="font-mono tabular-nums">{money(record.usage.cost_usd)}</span>
              ) : null}
            </span>
          </li>
        );
      })}

      {revisions.length > 0 && (
        <li className="flex items-center gap-3 bg-surface-2 px-4 py-2.5">
          <span className="w-28 shrink-0 text-sm font-medium">Revise</span>
          <StatusBadge tone={TONE[revisions[revisions.length - 1]!.status]}>
            {revisions.length} round{revisions.length === 1 ? "" : "s"}
          </StatusBadge>
          <span className="text-xs text-muted">
            red team sent the draft back
          </span>
        </li>
      )}
    </ol>
  );
}
